/** Paths that decide whether the video-settings footer may offer Undo. */
export type VideoSettingsUndoPath =
  | 'video-commit-success'
  | 'device-only'
  | 'video-commit-failure'
  | 'subsequent-edit';

/** Draft slice the undo pathway mutates. */
export type VideoSettingsUndoDraft<TCandidate> = {
  flags: { canUndo: boolean };
  lifecycle: { rollback: TCandidate | undefined; snapshot: TCandidate };
};

/**
 * Undo is armed only after a video replace that landed.
 * Device-only saves, failed replaces, and any later edit stay disarmed.
 */
export const shouldArmVideoSettingsUndo = (path: VideoSettingsUndoPath): boolean =>
  path === 'video-commit-success';

/**
 * Apply the undo pathway to the settings draft.
 * Success snapshots the pre-commit candidate so Undo can restore it.
 */
export const applyVideoSettingsUndoPath = <TCandidate>(
  draft: VideoSettingsUndoDraft<TCandidate>,
  path: VideoSettingsUndoPath,
  candidate?: TCandidate
): void => {
  draft.flags.canUndo = shouldArmVideoSettingsUndo(path);

  if (path === 'video-commit-success' && candidate !== undefined) {
    draft.lifecycle.rollback = draft.lifecycle.snapshot;
    // Caller supplies the post-commit snapshot clone.
    draft.lifecycle.snapshot = candidate;
    return;
  }

  // A later edit invalidates the post-save Undo snapshot.
  if (path === 'subsequent-edit') {
    draft.lifecycle.rollback = undefined;
  }
};
